<template>
    <div>

        <vue-headful title="Safety Data Sheets :: MeritEHS"/>

        <h4 class="py-3 mb-4">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item active"><i class="fa fa-files-o"></i> Safety Data Sheets</li>
                <li class="ml-3">
                    <router-link :to="`/safety-data-sheets/create`" class="btn btn-sm btn-success"><i class="fa fa-plus-circle"></i> Create</router-link>
                </li>
                <li class="ml-2">
                    <b-dropdown size="sm" variant="light" :disabled="generating">
                        <template #button-content>
                            <template v-if="generating">
                                <i class="fa fa-spin fa-spinner"></i> Generating
                            </template>
                            <template v-else>
                                <i class="fa fa-files-o"></i> Actions
                            </template>
                        </template>
                        <b-dropdown-item v-on:click="$router.push('/safety-data-sheets/import-zip')"><i class="fa fa-file-zip-o"></i> Import Zip File</b-dropdown-item>
                    </b-dropdown>
                </li>
            </ol>
        </h4>

        <ul class="search-nav nav nav-tabs tabs-alt container-p-x container-m--x mb-4 d-lg-flex d-none" v-if="canManage">
            <b-nav-item :active="curTab === 'list'" @click="curTab = 'list'">
                Data Sheets ({{ sheet_count }})
            </b-nav-item>
            <b-nav-item :active="curTab === 'catalogs'" @click="curTab = 'catalogs'">
                Catalogs ({{ catalogs.length }})
            </b-nav-item>
            <b-nav-item :active="curTab === 'settings'" @click="curTab = 'settings'">
                Public Access Settings
            </b-nav-item>
        </ul>

        <div class="mb-2 mb-lg-0 d-lg-none d-block" v-if="canManage">
            <b-dropdown :html="getTabTitle" variant="default" class="bg-white">
                <b-dropdown-item :active="curTab === 'list'" @click="curTab = 'list'">
                    Data Sheets ({{ sheet_count }})
                </b-dropdown-item>
                <b-dropdown-item :active="curTab === 'catalogs'" @click="curTab = 'catalogs'">
                    Catalogs ({{ catalogs.length }})
                </b-dropdown-item>
                <b-dropdown-item :active="curTab === 'settings'" @click="curTab = 'settings'">
                    Public Access Settings
                </b-dropdown-item>
            </b-dropdown>
        </div>

        <div class="card" v-show="curTab == 'list'">
            <div class="card-body">
                <safety-data-sheets-list ref="dataSheetIndexList" :showAddBtn="true" @sdsListCount="setCount" :showFilters="true"></safety-data-sheets-list>
            </div>
        </div>
        <template v-if="canManage">
            <div class="card" v-show="curTab == 'catalogs'">
                <div class="card-body">
                    <v-client-table ref="sdsCatalogTable" :data="catalogs" :columns="catalogColumns" :options="catalogOptions">

                        <div slot="afterFilterWrapper" class="ml-md-2 ml-0 mt-md-0 mt-2 mb-md-0 mb-3 float-md-left">
                            <div class="clearfix d-md-none d-block"></div>

                            <router-link :to="`/safety-data-sheets/catalogs/create`" class="btn btn-outline-success ml-md-2 ml-0" title="Add a Data Sheet Catalog">
                                <i class="fa fa-plus-circle"></i> Add
                            </router-link>

                            <div class="clearfix d-md-none d-block"></div>
                        </div>

                        <template slot="id" slot-scope="props">
                            <b-dropdown html='<i class="fa fa-ellipsis-h"></i>' variant="default" :no-flip="true" size="sm">
                                <b-dropdown-item href="javascript:void(0)" v-on:click="$router.push(`/safety-data-sheets/catalogs/edit/${props.row.id}`)" title="View Safety Data Sheet"><i class="fa fa-edit"></i> Edit</b-dropdown-item>
                                <b-dropdown-item href="javascript:void(0)" v-on:click="$router.push(`/safety-data-sheets/catalogs/delete/${props.row.id}`)" title="Edit Safety Data Sheet"><i class="fa fa-trash text-danger"></i> Delete</b-dropdown-item>
                            </b-dropdown>
                        </template>

                        <router-link slot="catalog_name" slot-scope="props" :to="`/safety-data-sheets/catalogs/view/${props.row.id}`" title="View Data Sheet Catalog">
                            {{ props.row.catalog_name }}
                        </router-link>

                    </v-client-table>
                </div>
            </div>

            <div class="card" v-show="curTab == 'settings'">
                <div class="card-body">
                    <div class="alert alert-info mb-2">
                        <strong>Public Access</strong> is a feature that allows non-users to access your data sheet library. This is useful for immediate on-site access and to maintain OSHA compliance.
                    </div>
                    <div class="row mt-2 mb-3">
                        <label class="col-md-3 text-md-right">Public Access URL:</label>
                        <div class="col-md-8">
                            <span v-if="generating"><i class="fa fa-spin fa-spinner"></i> Generating</span>
                            <span v-else-if="!account.sds_key">Enable Public Access via the Checkbox below to generate your Public Access URL</span>
                            <a v-else :href="`/sds/key/${account.sds_key}`" target="_blank" title="View Public Access Library in a New Tab">{{ $root.appUrl }}/sds/key/{{ account.sds_key }}</a>
                        </div>
                    </div>
                    <div class="row mt-2 mb-3">
                        <label class="col-md-3 text-md-right">Public Access QR Code:</label>
                        <div class="col-md-8">
                            <div v-if="account.sds_key">
                                <b-input-group class="mb-2">
                                    <template #prepend>
                                        <b-input-group-text class="bg-light">Size</b-input-group-text>
                                    </template>
                                    <b-form-select v-model="qr_code_size" :options="qrCodeOptions"></b-form-select>
                                </b-input-group>

                                <span v-show="qr_loading"><i class="fa fa-spin fa-spinner"></i></span>

                                <div v-show="!qr_loading">
                                    <img :src="`data:image/png;base64,${qr_code_html}`" />
                                </div>

                                <div class="mt-2">
                                    <a :href="`/safety-data-sheets/qr-code?download=true&qr_code_size=${qr_code_size}`" target="_blank">Download QR Code</a>
                                </div>
                            </div>
                            <div v-else>
                                -- Enable Public Access to Generate QR Code --
                            </div>
                        </div>
                    </div>
                    <div class="row mb-3">
                        <label class="col-md-3 text-md-right">
                            Enable Public Access:
                            <i class="fa fa-question-circle" title="You this setting to turn off and on Public Access. If unchecked, the Public Access URL and QR Code above will not work."></i>
                        </label>
                        <div class="col-md-8">
                            <b-form-checkbox v-model="account.enable_sds_public_access" :value="1" :unchecked-value="0" plain :disabled="generating" />
                        </div>
                    </div>
                    <div class="row mb-3">
                        <label class="col-md-3 text-md-right">
                            Require Employee Verification:
                            <i class="fa fa-question-circle" title="If checked, the person accessing the public link must provide an Employee#, Email Address, or Phone# that matches an employee recrod in MeritEHS."></i>
                        </label>
                        <div class="col-md-8">
                            <b-form-checkbox v-model="account.require_sds_verification" :value="1" :unchecked-value="0" plain :disabled="generating" />

                            <div class="mt-2 alert alert-warning" v-if="account.require_sds_verification === 0">
                                <strong>Warning:</strong> Turnning off the Employee Verification means that anyone with the SDS link can view the SDS library.
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <button type="button" v-on:click="saveAccount()" class="btn btn-sm btn-success" :disabled="saving">
                        <template v-if="saving"><i class="fa fa-spin fa-spinner"></i> Saving</template>
                        <template v-else>Save Settings</template>
                    </button>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    export default {
        data: function() {
            return {
                curTab: 'list',
                saving: false,
                generating: false,
                surpress_save: false,
                sheet_count: null,
                account: {},

                qr_loading: false,
                qr_code_size: 6,
                qr_code_html: null,
                qrCodeOptions: [
                    {value: 3, text: '87x87'},
                    {value: 4, text: '116x116'},
                    {value: 5, text: '145x145'},
                    {value: 6, text: '174x174'},
                    {value: 7, text: '203x203'},
                    {value: 8, text: '232x232'},
                    {value: 9, text: '261x261'},
                ],

                catalogs: [],
                catalogColumns: ['id', 'catalog_name'],
                catalogOptions: {
                    headings: {
                        id: 'Actions',
                        catalog_name: 'Catalog Name',
                    },
                    perPageValues: [],
                    sortable: ['catalog_name'],
                    columnsDropdown: false,
                    uniqueKey: 'id',
                },
            }
        },

        watch: {
            'account.enable_sds_public_access'(newVal, oldVal){
                //If check and the account sds is null
                if(newVal === 1 && oldVal === 0 && !this.account.sds_key)
                {
                    this.generateKey();
                }
            },

            qr_code_size(newVal, oldVal){
                //Get the QR Code
                this.getQrCode();
            },
        },

        computed: {
            canManage(){
                return this.$store.state.user.is_admin;
            },

            getTabTitle(){
                //Setup
                var title = '';
                var icon = 'bars';

                //Switch
                switch(this.curTab)
                {
                    case 'list':
                        //Set
                        title = 'Data Sheets ('+this.sheet_count+')';
                        break;
                    case 'catalogs':
                        //Set
                        title = 'Catalogs ('+this.catalogs.length+')';
                        break;
                    case 'settings':
                        //Set
                        title = 'Public Access Settings';
                        break;
                }

                //Send it
                return '<i class="fa fa-'+icon+'"></i> ' + title;
            }
        },

        methods: {
            //Get the Account
            getAccount(){
                //Helper
                this.surpress_save = true;

                //Get the data
                this.$http
                    .get('/safety-data-sheets/account')
                    .then(response => {
                        //Set all the data
                        this.account = response.data.account;
                        this.catalogs = response.data.catalogs;

                        //Reset
                        this.surpress_save = false;

                        //Check for SES Key
                        if(this.account.sds_key)
                        {
                            //Get the QR Code
                            this.getQrCode();
                        }
                    })
                    .catch(response => {

                    })
                    .then(() => {

                    });
            },

            saveAccount(){
                //Setup
                this.saving = true;

                //Send the data
                this.$http
                    .post('/safety-data-sheets/account', this.account)
                    .then(response => {
                        //Set all the data
                        this.$toastr.success('Public Access Settings Saved');
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Reset
                        this.saving = false;
                    });
            },

            generateKey(){
                //Setup
                this.generating = true;

                //Send the data
                this.$http
                    .post('/safety-data-sheets/generate-key')
                    .then(response => {
                        //Set the key
                        this.account.sds_key = response.data;

                        //Save the account
                        this.saveAccount();
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Reset
                        this.generating = false;
                    });
            },

            setCount(count){
                //Set the count
                this.sheet_count = count;
            },

            getQrCode(){
                //QR Code
                this.qr_loading = true;

                //Get the data
                this.$http
                    .get('/safety-data-sheets/qr-code', {qr_code_size: this.qr_code_size})
                    .then(response => {
                        //Set all the data
                        this.qr_code_html = response.data;
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Set loading
                        this.qr_loading = false;
                    });
            },
        },

        mounted(){
            //Check Admin
            if(this.canManage)
            {
                //Get the Settings Stuff
                this.getAccount();

                //Check for HASH
                if (this.$route.hash !== '' && this.$route.hash !== '#')
                {
                    this.curTab = this.$route.hash.replace('#','')
                }
            }
            else
            {
                //Focus on the SEarch Input
                this.$refs.dataSheetIndexList.$refs.safetyDataSheetTable.$refs.genericFilter.focus();
            }
        },
    }
</script>
